import { z } from "zod";

import { IntIdSchema } from "../common/dto";

/**
 * Purchase-guard DTOs (CREW-559)
 * Shared contracts for the duplicate-purchase guard: the 409 already_purchased
 * payload returned by POST /payment/invoice and POST /payment/stripe, and the
 * guard's input item shape.
 */

/** Machine-readable `message` value of the guard's 409 payload. */
export const ALREADY_PURCHASED_ERROR = "already_purchased";

// ============= Guard input =============

/**
 * One shopping-trolley item as submitted to checkout: a course (or bundle)
 * being bought for one user. crewId scopes bundle-subscription lookups.
 */
export const ShoppingItemInputSchema = z.object({
  courseId: IntIdSchema,
  userId: IntIdSchema,
  crewId: IntIdSchema.optional(),
  isBundle: z.boolean().optional().default(false),
});
export type ShoppingItemInput = z.input<typeof ShoppingItemInputSchema>;

// ============= 409 payload =============

/**
 * A trolley item already covered by an open purchase: a paid/processing order
 * item with no newer completion of the course (renewals of completed courses
 * stay purchasable), or an active, unexpired bundle subscription.
 */
export const PurchasedItemConflictSchema = z.object({
  courseId: IntIdSchema,
  userId: IntIdSchema,
  isBundle: z.boolean(),
});
export type PurchasedItemConflict = z.infer<typeof PurchasedItemConflictSchema>;

/** Body of the 409 returned by checkout endpoints guarded by the purchase guard. */
export const AlreadyPurchasedResponseSchema = z.object({
  error: z.literal("Conflict"),
  message: z.literal(ALREADY_PURCHASED_ERROR),
  conflicts: z.array(PurchasedItemConflictSchema),
});
export type AlreadyPurchasedResponse = z.infer<typeof AlreadyPurchasedResponseSchema>;
